import { useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Card,
  Group,
  Portal,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { IconTrash, IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabaseClient';

function readControlValue(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): string {
  return event.target?.value ?? '';
}

function readCheckboxValue(event: React.ChangeEvent<HTMLInputElement>): boolean {
  return event.target?.checked ?? false;
}
import type { ScheduleCategory } from './AblaufplanCategoryManager';
import type { ScheduleEntry } from './AblaufplanEntryModal';

// ── Constants ─────────────────────────────────────────────────────────────────
const CELL_H = 64;        // px per hour
const DAY_START = 8;      // 08:00
const DAY_END = 22;       // 22:00
const SNAP = 15;          // minute snap
const MIN_DUR = 15;       // minimum block duration (min)
const TOTAL_H = (DAY_END - DAY_START) * CELL_H;
const COL_MIN_W = 160;    // minimum column width px
const TIME_W = 52;        // time axis width px
const HOURS = Array.from({ length: DAY_END - DAY_START + 1 }, (_, i) => DAY_START + i);

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeToY(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return ((h * 60 + m) - DAY_START * 60) / 60 * CELL_H;
}

function yToSnapped(y: number): string {
  const clamped = Math.max(0, Math.min(y, TOTAL_H - (MIN_DUR / 60) * CELL_H));
  const snappedMin = Math.round((clamped / CELL_H * 60) / SNAP) * SNAP;
  const total = DAY_START * 60 + snappedMin;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function timeToMin(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function minToTime(total: number): string {
  const c = Math.max(DAY_START * 60, Math.min(DAY_END * 60, total));
  return `${String(Math.floor(c / 60)).padStart(2, '0')}:${String(c % 60).padStart(2, '0')}`;
}

function blockH(start: string, end: string): number {
  return Math.max((MIN_DUR / 60) * CELL_H, (timeToMin(end) - timeToMin(start)) / 60 * CELL_H);
}

function hexRgba(hex: string, a: number): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

// ── Drag state (ref-based to avoid stale closures) ────────────────────────────
type DragState =
  | { type: 'idle' }
  | { type: 'moving'; entry: ScheduleEntry; sx: number; sy: number; dx: number; dy: number; catId: string; moved: boolean }
  | { type: 'resizing'; entry: ScheduleEntry; sy: number; dy: number }
  | { type: 'creating'; catId: string; startY: number; currY: number };

// ── Quick-edit state ───────────────────────────────────────────────────────────
type QuickEdit = {
  entry: ScheduleEntry;
  x: number; y: number;
  title: string; location: string; description: string; visible: boolean;
};

// ── Props ─────────────────────────────────────────────────────────────────────
type Props = {
  entries: ScheduleEntry[];
  categories: ScheduleCategory[];
  selectedDate: string;
  formId: string;
  highlightedEntryIds?: string[];
  selectionMode?: boolean;
  selectedEntryIds?: string[];
  onToggleSelection?: (entryId: string) => void;
  onOpenFullEdit?: (entry: ScheduleEntry) => void;
};

// ── Component ─────────────────────────────────────────────────────────────────
export function AblaufplanCalendarView({
  entries,
  categories,
  selectedDate,
  formId,
  highlightedEntryIds,
  selectionMode = false,
  selectedEntryIds = [],
  onToggleSelection,
  onOpenFullEdit,
}: Props) {
  const qc = useQueryClient();
  const drag = useRef<DragState>({ type: 'idle' });
  const [, setTick] = useState(0); // force re-render on drag state change
  const [quickEdit, setQuickEdit] = useState<QuickEdit | null>(null);
  const colRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollRef = useRef<HTMLDivElement>(null);
  const quickEditRef = useRef<HTMLDivElement>(null);
  const highlighted = new Set(highlightedEntryIds ?? []);
  const selected = new Set(selectedEntryIds);

  // ── Undo state ─────────────────────────────────────────────────────────────
  const undoRef = useRef<{ originalEntry: ScheduleEntry } | null>(null);
  const entriesRef = useRef(entries);
  entriesRef.current = entries; // always current, safe to read from stable closures

  const formIdRef = useRef(formId);
  formIdRef.current = formId;

  function setDrag(next: DragState) {
    drag.current = next;
    setTick(t => t + 1);
  }

  const dayEntries = entries.filter(e => e.entry_date === selectedDate);

  // "Alle"-Kategorie: Einträge darin spannen die volle Breite
  const alleCategory = categories.find(c => c.name === 'Alle');
  const alleEntries = alleCategory
    ? dayEntries.filter(e => e.category_id === alleCategory.id)
    : [];
  const regularEntries = dayEntries.filter(e => e.category_id !== alleCategory?.id);

  const uncategorized = regularEntries.filter(e => !e.category_id);
  // Regular columns: exclude "Alle" category (it renders as overlay)
  const regularCols: Array<ScheduleCategory | { id: '__none__'; name: string; color: string; icon: null }> = [
    ...categories.filter(c => c.name !== 'Alle'),
    ...(uncategorized.length > 0 ? [{ id: '__none__' as const, name: 'Ohne Kategorie', color: '#888888', icon: null }] : []),
  ];
  // Keep "columns" for legacy references (header guard)
  const columns = alleCategory ? [alleCategory, ...regularCols] : regularCols;

  // ── Close quick-edit on click-outside ────────────────────────────────────
  useEffect(() => {
    if (!quickEdit) return;
    function onDown(e: MouseEvent) {
      if (quickEditRef.current && !quickEditRef.current.contains(e.target as Node)) {
        setQuickEdit(null);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [quickEdit]);

  // ── Global drag listeners (stable, read from refs) ────────────────────────
  useEffect(() => {
    function findCatUnderX(clientX: number): string {
      for (const [id, el] of colRefs.current) {
        const r = el.getBoundingClientRect();
        if (clientX >= r.left && clientX < r.right) return id;
      }
      const ds = drag.current;
      return ds.type === 'moving' ? ds.catId : '';
    }

    function onMove(e: MouseEvent) {
      const ds = drag.current;
      if (ds.type === 'idle') return;
      e.preventDefault();

      if (ds.type === 'moving') {
        const dx = e.clientX - ds.sx;
        const dy = e.clientY - ds.sy;
        const moved = Math.abs(dx) > 4 || Math.abs(dy) > 4;
        const catId = findCatUnderX(e.clientX) || ds.catId;
        setDrag({ ...ds, dx, dy, catId, moved });
      } else if (ds.type === 'resizing') {
        setDrag({ ...ds, dy: e.clientY - ds.sy });
      } else if (ds.type === 'creating') {
        const col = colRefs.current.get(ds.catId);
        if (!col) return;
        const rect = col.getBoundingClientRect();
        const currY = e.clientY - rect.top + (scrollRef.current?.scrollTop ?? 0);
        setDrag({ ...ds, currY });
      }
    }

    async function onUp(e: MouseEvent) {
      const ds = drag.current;
      setDrag({ type: 'idle' });
      if (ds.type === 'idle') return;

      if (ds.type === 'moving') {
        if (!ds.moved) {
          // click → quick edit
          setQuickEdit({
            entry: ds.entry,
            x: e.clientX,
            y: e.clientY,
            title: ds.entry.title,
            location: ds.entry.location ?? '',
            description: ds.entry.description ?? '',
            visible: ds.entry.is_app_visible,
          });
        } else {
          const originalEntry = ds.entry;
          const newStartY = timeToY(ds.entry.start_time) + ds.dy;
          const newStart = yToSnapped(newStartY);
          const dur = timeToMin(ds.entry.end_time) - timeToMin(ds.entry.start_time);
          const newEnd = minToTime(timeToMin(newStart) + dur);
          const newCat = ds.catId === '__none__' ? null : ds.catId;
          await doUpdate(ds.entry, { p_start_time: newStart, p_end_time: newEnd, p_category_id: newCat });
          showUndoToast(originalEntry, 'Eintrag verschoben');
        }
      } else if (ds.type === 'resizing') {
        const originalEntry = ds.entry;
        const rawEndY = timeToY(ds.entry.end_time) + ds.dy;
        const minEndY = timeToY(ds.entry.start_time) + (MIN_DUR / 60) * CELL_H;
        const newEnd = yToSnapped(Math.max(minEndY, rawEndY));
        const finalEnd = newEnd <= ds.entry.start_time
          ? minToTime(timeToMin(ds.entry.start_time) + MIN_DUR)
          : newEnd;
        await doUpdate(ds.entry, { p_end_time: finalEnd });
        showUndoToast(originalEntry, 'Eintrag angepasst');
      } else if (ds.type === 'creating') {
        const startTime = yToSnapped(Math.min(ds.startY, ds.currY));
        const rawEnd = Math.max(ds.currY, ds.startY + (MIN_DUR / 60) * CELL_H);
        const endTime = yToSnapped(rawEnd);
        const finalEnd = endTime <= startTime ? minToTime(timeToMin(startTime) + 60) : endTime;
        await doCreate(ds.catId === '__none__' ? null : ds.catId, startTime, finalEnd);
      }
    }

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── DB helpers ────────────────────────────────────────────────────────────
  async function doUpdate(
    entry: ScheduleEntry,
    overrides: Partial<{
      p_title: string; p_description: string | null; p_location: string | null;
      p_start_time: string; p_end_time: string; p_category_id: string | null;
      p_is_app_visible: boolean;
    }>,
  ) {
    // Optimistic update: apply to cache immediately so UI doesn't flash
    const queryKey = ['schedule-entries', formIdRef.current];
    const merged = { ...entry, ...Object.fromEntries(
      Object.entries(overrides).map(([k, v]) => [k.replace('p_', ''), v])
    )};
    const prev = qc.getQueryData<ScheduleEntry[]>(queryKey);
    qc.setQueryData<ScheduleEntry[]>(queryKey, (old) =>
      old?.map((e) => e.id === entry.id ? { ...e, ...merged } : e) ?? []
    );

    const res = await supabase.rpc('update_schedule_entry', {
      p_id: entry.id, p_expected_version: entry.version,
      p_title: entry.title, p_description: entry.description, p_location: entry.location,
      p_entry_date: entry.entry_date, p_start_time: entry.start_time, p_end_time: entry.end_time,
      p_category_id: entry.category_id, p_sort_order: entry.sort_order,
      p_is_app_visible: entry.is_app_visible, p_visible_until: entry.visible_until,
      ...overrides,
    });
    const data = res.data as { ok: boolean; error?: string } | null;
    if (!data?.ok) {
      // Revert optimistic update on error
      qc.setQueryData(queryKey, prev);
      notifications.show({
        color: data?.error === 'conflict' ? 'orange' : 'red',
        title: data?.error === 'conflict' ? 'Konflikt' : 'Fehler',
        message: data?.error === 'conflict'
          ? 'Ein anderer Admin hat diesen Eintrag bereits geändert.'
          : (data?.error ?? 'Unbekannter Fehler'),
      });
    } else {
      // Sync version in cache after success
      qc.setQueryData<ScheduleEntry[]>(queryKey, (old) =>
        old?.map((e) => e.id === entry.id ? { ...e, version: (data as any).version ?? e.version + 1 } : e) ?? []
      );
    }
  }

  async function doCreate(categoryId: string | null, startTime: string, endTime: string) {
    const { data: row, error } = await supabase
      .from('schedule_entries')
      .insert({
        form_id: formIdRef.current,
        category_id: categoryId,
        entry_date: selectedDate,
        start_time: startTime,
        end_time: endTime,
        title: 'Neuer Eintrag',
        is_app_visible: true,
      })
      .select('id')
      .single();
    if (error) { notifications.show({ color: 'red', title: 'Fehler', message: error.message }); return; }
    if (row) {
      await supabase.from('schedule_entry_assignments').insert({ entry_id: row.id, assignment_type: 'all' });
    }
    qc.invalidateQueries({ queryKey: ['schedule-entries', formIdRef.current] });
  }

  function showUndoToast(originalEntry: ScheduleEntry, label: string) {
    undoRef.current = { originalEntry };
    notifications.show({
      id: 'schedule-undo',
      title: label,
      message: (
        <span>
          {label}.{' '}
          <strong
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={async () => {
              const undo = undoRef.current;
              if (!undo) return;
              undoRef.current = null;
              notifications.hide('schedule-undo');
              const current = entriesRef.current.find((e) => e.id === undo.originalEntry.id);
              if (!current) return;
              await doUpdate(current, {
                p_start_time: undo.originalEntry.start_time,
                p_end_time: undo.originalEntry.end_time,
                p_category_id: undo.originalEntry.category_id,
              });
            }}
          >
            Rückgängig
          </strong>
        </span>
      ),
      color: 'blue',
      autoClose: 5000,
      withCloseButton: true,
      onClose: () => { undoRef.current = null; },
    });
  }

  async function doDelete(entry: ScheduleEntry) {
    if (!window.confirm(`"${entry.title}" löschen?`)) return;
    await supabase.from('schedule_entries').delete().eq('id', entry.id);
    qc.invalidateQueries({ queryKey: ['schedule-entries', formIdRef.current] });
    setQuickEdit(null);
  }

  async function saveQuickEdit() {
    if (!quickEdit) return;
    await doUpdate(quickEdit.entry, {
      p_title: quickEdit.title,
      p_location: quickEdit.location || null,
      p_description: quickEdit.description || null,
      p_is_app_visible: quickEdit.visible,
    });
    setQuickEdit(null);
  }

  // ── Visual position during drag ───────────────────────────────────────────
  function getVisual(entry: ScheduleEntry): { top: number; h: number; catId: string | null } {
    const ds = drag.current;
    if (ds.type === 'moving' && ds.entry.id === entry.id) {
      const newStart = yToSnapped(timeToY(ds.entry.start_time) + ds.dy);
      const dur = timeToMin(ds.entry.end_time) - timeToMin(ds.entry.start_time);
      const newEnd = minToTime(timeToMin(newStart) + dur);
      return { top: timeToY(newStart), h: blockH(newStart, newEnd), catId: ds.catId === '__none__' ? null : ds.catId };
    }
    if (ds.type === 'resizing' && ds.entry.id === entry.id) {
      const rawEndY = timeToY(ds.entry.end_time) + ds.dy;
      const minEndY = timeToY(ds.entry.start_time) + (MIN_DUR / 60) * CELL_H;
      const newEnd = yToSnapped(Math.max(minEndY, rawEndY));
      return { top: timeToY(ds.entry.start_time), h: blockH(ds.entry.start_time, newEnd), catId: entry.category_id };
    }
    return { top: timeToY(entry.start_time), h: blockH(entry.start_time, entry.end_time), catId: entry.category_id };
  }

  const ds = drag.current; // snapshot for render

  if (columns.length === 0) {
    return (
      <Text c="dimmed" size="sm" ta="center" py="xl">
        Erstelle zuerst Kategorien, um den Kalender zu nutzen.
      </Text>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '72vh', minHeight: 500, userSelect: 'none' }}>
      {/* Column headers — single row; "Alle" included as regular column so widths stay in sync with body */}
      <div style={{ display: 'flex', flexShrink: 0, borderBottom: '1px solid var(--mantine-color-default-border)' }}>
        <div style={{ width: TIME_W, flexShrink: 0 }} />
        <div style={{ display: 'flex', flex: 1, overflowX: 'hidden' }}>
          {/* "Alle" column header — same single-line style as other columns */}
          {alleCategory && (
            <div style={{
              flex: `1 0 ${COL_MIN_W}px`,
              padding: '6px 10px',
              background: hexRgba(alleCategory.color, 0.18),
              borderLeft: `3px solid ${alleCategory.color}`,
              marginLeft: 1,
            }}>
              <Text size="xs" fw={700} style={{ color: alleCategory.color }}>
                {alleCategory.icon ? `${alleCategory.icon} ` : ''}{alleCategory.name}
              </Text>
            </div>
          )}
          {regularCols.map(col => (
            <div
              key={col.id}
              style={{
                flex: `1 0 ${COL_MIN_W}px`,
                padding: '6px 10px',
                background: hexRgba(col.color, 0.15),
                borderLeft: `3px solid ${col.color}`,
                marginLeft: 1,
              }}
            >
              <Text size="xs" fw={700} style={{ color: col.color }}>
                {col.icon ? `${col.icon} ` : ''}{col.name}
              </Text>
            </div>
          ))}
        </div>
      </div>

      {/* Scrollable body */}
      <div ref={scrollRef} style={{ display: 'flex', flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
        {/* Time axis */}
        <div style={{ width: TIME_W, flexShrink: 0, position: 'relative', height: TOTAL_H }}>
          {HOURS.map(h => (
            <div
              key={h}
              style={{
                position: 'absolute',
                top: (h - DAY_START) * CELL_H - 7,
                right: 6,
                fontSize: 10,
                color: 'var(--mantine-color-dimmed)',
                lineHeight: 1,
                pointerEvents: 'none',
              }}
            >
              {String(h).padStart(2, '0')}:00
            </div>
          ))}
        </div>

        {/* Columns wrapper — includes full-width "Alle" overlay + regular columns */}
        <div style={{ position: 'relative', flex: 1, height: TOTAL_H }}>

          {/* ── Full-width "Alle" entries overlay ── */}
          {alleCategory && alleEntries.map(entry => {
            const vis = getVisual(entry);
            const active = (ds.type === 'moving' || ds.type === 'resizing') && ds.entry.id === entry.id;
            const hidden = !entry.is_app_visible;
            const isSelected = selected.has(entry.id);
            return (
              <div
                key={entry.id}
                data-entry
                style={{
                  position: 'absolute',
                  top: vis.top + 1,
                  height: Math.max(vis.h - 2, 20),
                  left: 0,
                  right: 0,
                  background: hexRgba(alleCategory.color, hidden ? 0.2 : 0.6),
                  border: `1px solid ${alleCategory.color}`,
                  borderRadius: 4,
                  padding: '2px 10px',
                  overflow: 'hidden',
                  cursor: selectionMode ? 'pointer' : active ? 'grabbing' : 'grab',
                  zIndex: active ? 20 : 8,
                  boxShadow: active
                    ? '0 6px 20px rgba(0,0,0,0.35)'
                    : isSelected
                      ? '0 0 0 2px var(--mantine-color-blue-5)'
                      : highlighted.has(entry.id)
                        ? '0 0 0 2px var(--mantine-color-red-5)'
                        : '0 1px 3px rgba(0,0,0,0.15)',
                  opacity: hidden ? 0.55 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
                onMouseDown={e => {
                  e.stopPropagation();
                  if (selectionMode) {
                    onToggleSelection?.(entry.id);
                    return;
                  }
                  // "Alle" entries: drag only changes time, not category
                  setDrag({ type: 'moving', entry, sx: e.clientX, sy: e.clientY, dx: 0, dy: 0, catId: alleCategory.id, moved: false });
                }}
              >
                <Text size="xs" fw={700} style={{ color: '#fff', pointerEvents: 'none', fontSize: 11 }}>
                  {alleCategory.icon} {entry.title}
                </Text>
                <Text size="xs" style={{ color: 'rgba(255,255,255,0.75)', pointerEvents: 'none', fontSize: 10 }}>
                  {entry.start_time.slice(0, 5)}–{entry.end_time.slice(0, 5)}
                  {entry.location ? `  📍 ${entry.location}` : ''}
                </Text>
                {/* Resize handle */}
                <div
                  style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 6, cursor: selectionMode ? 'pointer' : 'ns-resize', background: 'rgba(0,0,0,0.2)', borderRadius: '0 0 4px 4px' }}
                  onMouseDown={e => {
                    e.stopPropagation();
                    if (selectionMode) {
                      onToggleSelection?.(entry.id);
                      return;
                    }
                    setDrag({ type: 'resizing', entry, sy: e.clientY, dy: 0 });
                  }}
                />
              </div>
            );
          })}

          {/* ── All columns (incl. Alle) so header + body widths match ── */}
          <div style={{ display: 'flex', height: TOTAL_H }}>
          {columns.map(col => {
            const isAlleCol = alleCategory != null && col.id === alleCategory.id;
            // Alle entries are rendered as full-width overlay above; column shows only grid
            const colEntries = isAlleCol
              ? []
              : regularEntries.filter(e =>
                  col.id === '__none__' ? !e.category_id : e.category_id === col.id,
                );
            const isDragOver = ds.type === 'moving' && ds.catId === col.id && ds.moved;
            const isCreating = ds.type === 'creating' && ds.catId === col.id;

            return (
              <div
                key={col.id}
                ref={el => {
                  if (el) colRefs.current.set(col.id, el);
                  else colRefs.current.delete(col.id);
                }}
                style={{
                  flex: `1 0 ${COL_MIN_W}px`,
                  position: 'relative',
                  height: TOTAL_H,
                  borderLeft: `1px solid var(--mantine-color-default-border)`,
                  background: isDragOver ? hexRgba(col.color, 0.08) : 'transparent',
                  cursor: ds.type === 'idle' ? 'crosshair' : 'default',
                  transition: 'background 0.15s',
                }}
                onMouseDown={e => {
                  if (selectionMode) return;
                  if ((e.target as HTMLElement).closest('[data-entry]')) return;
                  const el = colRefs.current.get(col.id);
                  if (!el) return;
                  const rect = el.getBoundingClientRect();
                  const y = e.clientY - rect.top + (scrollRef.current?.scrollTop ?? 0);
                  setDrag({ type: 'creating', catId: col.id, startY: y, currY: y });
                }}
              >
                {/* Hour lines */}
                {HOURS.map(h => (
                  <div key={h} style={{
                    position: 'absolute', top: (h - DAY_START) * CELL_H,
                    left: 0, right: 0, pointerEvents: 'none',
                    borderTop: '1px solid var(--mantine-color-default-border)',
                  }} />
                ))}
                {/* Half-hour lines */}
                {HOURS.slice(0, -1).map(h => (
                  <div key={`${h}.5`} style={{
                    position: 'absolute', top: (h - DAY_START) * CELL_H + CELL_H / 2,
                    left: 0, right: 0, pointerEvents: 'none',
                    borderTop: '1px dashed var(--mantine-color-default-border)', opacity: 0.5,
                  }} />
                ))}

                {/* Ghost while creating */}
                {isCreating && (
                  <div style={{
                    position: 'absolute',
                    top: Math.min(ds.startY, ds.currY),
                    height: Math.max((MIN_DUR / 60) * CELL_H, Math.abs(ds.currY - ds.startY)),
                    left: 3, right: 3,
                    background: hexRgba(col.color, 0.3),
                    border: `2px dashed ${col.color}`,
                    borderRadius: 4,
                    pointerEvents: 'none',
                  }} />
                )}

                {/* Entry blocks */}
                {colEntries.map(entry => {
                  const vis = getVisual(entry);
                  const isThisCat = col.id === '__none__' ? vis.catId === null : vis.catId === col.id;
                  if (!isThisCat) return null;
                  const active = (ds.type === 'moving' || ds.type === 'resizing') && ds.entry.id === entry.id;
                  const hidden = !entry.is_app_visible;
                  const isSelected = selected.has(entry.id);

                  return (
                    <div
                      key={entry.id}
                      data-entry
                      style={{
                        position: 'absolute',
                        top: vis.top + 1,
                        height: vis.h - 2,
                        left: 3, right: 3,
                        background: hexRgba(col.color, hidden ? 0.3 : 0.8),
                        border: `1px solid ${col.color}`,
                        borderRadius: 4,
                        padding: '2px 6px 8px',
                        overflow: 'hidden',
                        cursor: selectionMode ? 'pointer' : active ? 'grabbing' : 'grab',
                        zIndex: active ? 20 : 1,
                        boxShadow: active
                          ? '0 6px 20px rgba(0,0,0,0.35)'
                          : isSelected
                            ? '0 0 0 2px var(--mantine-color-blue-5)'
                            : highlighted.has(entry.id)
                              ? '0 0 0 2px var(--mantine-color-red-5)'
                              : '0 1px 3px rgba(0,0,0,0.15)',
                        opacity: hidden ? 0.65 : 1,
                      }}
                      onMouseDown={e => {
                        e.stopPropagation();
                        if (selectionMode) {
                          onToggleSelection?.(entry.id);
                          return;
                        }
                        setDrag({ type: 'moving', entry, sx: e.clientX, sy: e.clientY, dx: 0, dy: 0, catId: col.id, moved: false });
                      }}
                    >
                      <Text size="xs" fw={700} lineClamp={1} style={{ color: '#fff', pointerEvents: 'none', fontSize: 11 }}>
                        {entry.title}
                      </Text>
                      <Text size="xs" style={{ color: 'rgba(255,255,255,0.8)', pointerEvents: 'none', fontSize: 10 }}>
                        {entry.start_time.slice(0, 5)}–{entry.end_time.slice(0, 5)}
                        {hidden ? ' · ausgeblendet' : ''}
                      </Text>
                      {/* Resize handle */}
                      <div
                        style={{
                          position: 'absolute', bottom: 0, left: 0, right: 0,
                          height: 6, cursor: selectionMode ? 'pointer' : 'ns-resize',
                          background: 'rgba(0,0,0,0.25)', borderRadius: '0 0 4px 4px',
                        }}
                        onMouseDown={e => {
                          e.stopPropagation();
                          if (selectionMode) {
                            onToggleSelection?.(entry.id);
                            return;
                          }
                          setDrag({ type: 'resizing', entry, sy: e.clientY, dy: 0 });
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
          </div>{/* end regular columns flex */}
        </div>{/* end columns wrapper */}
      </div>{/* end scrollable body */}

      {/* Quick-edit popover */}
      {quickEdit && (
        <Portal>
          <Card
            ref={quickEditRef}
            shadow="xl"
            p="sm"
            radius="md"
            withBorder
            style={{
              position: 'fixed',
              left: Math.min(quickEdit.x + 10, window.innerWidth - 300),
              top: Math.min(quickEdit.y - 10, window.innerHeight - 300),
              width: 284,
              zIndex: 2000,
            }}
          >
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={600}>Eintrag</Text>
                <Group gap={4}>
                  <ActionIcon size="sm" color="red" variant="subtle" onClick={() => doDelete(quickEdit.entry)}>
                    <IconTrash size={13} />
                  </ActionIcon>
                  <ActionIcon size="sm" variant="subtle" onClick={() => setQuickEdit(null)}>
                    <IconX size={13} />
                  </ActionIcon>
                </Group>
              </Group>
              <TextInput
                size="xs"
                placeholder="Titel"
                value={quickEdit.title}
                autoFocus
                onChange={e => { const v = readControlValue(e); setQuickEdit(q => q ? { ...q, title: v } : q); }}
                onKeyDown={e => { if (e.key === 'Enter') saveQuickEdit(); if (e.key === 'Escape') setQuickEdit(null); }}
              />
              <TextInput
                size="xs"
                placeholder="Ort (optional)"
                value={quickEdit.location}
                onChange={e => { const v = readControlValue(e); setQuickEdit(q => q ? { ...q, location: v } : q); }}
              />
              <Textarea
                size="xs"
                placeholder="Beschreibung (optional)"
                rows={2}
                value={quickEdit.description}
                onChange={e => { const v = readControlValue(e); setQuickEdit(q => q ? { ...q, description: v } : q); }}
              />
              <Switch
                size="xs"
                label="In App anzeigen"
                checked={quickEdit.visible}
                onChange={e => { const v = readCheckboxValue(e); setQuickEdit(q => q ? { ...q, visible: v } : q); }}
              />
              {onOpenFullEdit && (
                <Text
                  size="xs"
                  c="dimmed"
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => {
                    const entry = quickEdit.entry;
                    setQuickEdit(null);
                    onOpenFullEdit(entry);
                  }}
                >
                  👥 Zuordnung / vollständig bearbeiten →
                </Text>
              )}
              <Group justify="flex-end" gap="xs">
                <Text size="xs" c="dimmed" style={{ cursor: 'pointer' }} onClick={() => setQuickEdit(null)}>Abbrechen</Text>
                <Text size="xs" fw={600} c="blue" style={{ cursor: 'pointer' }} onClick={saveQuickEdit}>Speichern</Text>
              </Group>
            </Stack>
          </Card>
        </Portal>
      )}
    </div>
  );
}
